import { useInfiniteQuery, type InfiniteData, type QueryClient } from '@tanstack/react-query';
import { getNodeHistoryApi, type NodeHistoryPage } from '@/api/nodeHistory/nodeHistory.api';

/** Prefix for drawer pages + the workflow-level menu gate. */
export const nodeHistoryQueryKey = (workflowId: string) => ['nodeHistory', workflowId] as const;

export const nodeHistoryPagesQueryKey = (workflowId: string, nodeId: string | null) =>
  [...nodeHistoryQueryKey(workflowId), nodeId] as const;

/** Query key for the shared menu-gate id set (nodes with visible history). */
export const nodeHistoryNodeIdsQueryKey = (workflowId: string) =>
  [...nodeHistoryQueryKey(workflowId), 'nodeIds'] as const;

/** Invalidate drawer history + menu node-id set after capture / clear. */
export const invalidateNodeHistoryQueries = (queryClient: QueryClient, workflowId: string): Promise<void> =>
  queryClient.invalidateQueries({ queryKey: nodeHistoryQueryKey(workflowId) });

export type NodeHistoryInfiniteData = InfiniteData<NodeHistoryPage, string | undefined>;

export type OptimisticClearNodeHistorySnapshot = {
  previousPages: NodeHistoryInfiniteData | undefined;
  previousNodeIds: string[] | undefined;
};

/** Empty drawer pages + drop `nodeId` from the menu gate; snapshot for rollback on failure. */
export function optimisticallyClearNodeHistory(
  queryClient: QueryClient,
  workflowId: string,
  nodeId: string,
): OptimisticClearNodeHistorySnapshot {
  const pagesKey = nodeHistoryPagesQueryKey(workflowId, nodeId);
  const nodeIdsKey = nodeHistoryNodeIdsQueryKey(workflowId);
  const previousPages = queryClient.getQueryData<NodeHistoryInfiniteData>(pagesKey);
  const previousNodeIds = queryClient.getQueryData<string[]>(nodeIdsKey);

  queryClient.setQueryData<NodeHistoryInfiniteData>(pagesKey, {
    pages: [{ items: [], nextCursor: null }],
    pageParams: [undefined],
  });
  queryClient.setQueryData<string[]>(nodeIdsKey, (old) => (old ? old.filter((id) => id !== nodeId) : old));

  return { previousPages, previousNodeIds };
}

export function rollbackOptimisticClearNodeHistory(
  queryClient: QueryClient,
  workflowId: string,
  nodeId: string,
  snapshot: OptimisticClearNodeHistorySnapshot,
): void {
  queryClient.setQueryData(nodeHistoryPagesQueryKey(workflowId, nodeId), snapshot.previousPages);
  queryClient.setQueryData(nodeHistoryNodeIdsQueryKey(workflowId), snapshot.previousNodeIds);
}

export type OptimisticRenameNodeHistorySnapshot = {
  previousPages: NodeHistoryInfiniteData | undefined;
};

/** Patch one version's `title` across cached pages; snapshot for rollback on failure. */
export function optimisticallyRenameNodeHistoryVersion(
  queryClient: QueryClient,
  workflowId: string,
  nodeId: string,
  versionId: string,
  title: string | null,
): OptimisticRenameNodeHistorySnapshot {
  const pagesKey = nodeHistoryPagesQueryKey(workflowId, nodeId);
  const previousPages = queryClient.getQueryData<NodeHistoryInfiniteData>(pagesKey);

  queryClient.setQueryData<NodeHistoryInfiniteData>(pagesKey, (old) => {
    if (!old) {
      return old;
    }
    return {
      ...old,
      pages: old.pages.map((page) => ({
        ...page,
        items: page.items.map((item) => (item.id === versionId ? { ...item, title } : item)),
      })),
    };
  });

  return { previousPages };
}

export function rollbackOptimisticRenameNodeHistoryVersion(
  queryClient: QueryClient,
  workflowId: string,
  nodeId: string,
  snapshot: OptimisticRenameNodeHistorySnapshot,
): void {
  queryClient.setQueryData(nodeHistoryPagesQueryKey(workflowId, nodeId), snapshot.previousPages);
}

/** Prompt-node version history for the drawer: newest-first, cursor-paginated, flattened across pages. */
export const useNodeHistory = (workflowId: string, nodeId: string | null, enabled: boolean) => {
  const query = useInfiniteQuery({
    queryKey: nodeHistoryPagesQueryKey(workflowId, nodeId),
    enabled: enabled && !!nodeId,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => getNodeHistoryApi({ workflowId, nodeId: nodeId as string, cursor: pageParam }),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  // Expose the full query plus the flattened, newest-first items the drawer renders.
  return { ...query, items: query.data?.pages.flatMap((page) => page.items) ?? [] };
};
